import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Story", href: "#story" },
  { label: "Features", href: "#features" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/trainova-logo.png" alt="Trainova" className="h-9 w-9 rounded-sm object-cover" />
          <span className="font-condensed text-2xl leading-none tracking-widest">TRAINOVA</span>
        </a>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="group relative text-sm font-medium tracking-wide text-zinc-300 transition-colors duration-200 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-teal transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
          <Link
            to="/faq"
            data-testid="nav-link-faq"
            className="group relative text-sm font-medium tracking-wide text-zinc-300 transition-colors duration-200 hover:text-white"
          >
            FAQ
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-teal transition-all duration-300 group-hover:w-full" />
          </Link>
          <Link
            to="/download"
            data-testid="nav-waitlist-btn"
            className="group relative overflow-hidden bg-teal px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-black transition-transform duration-200 hover:scale-[1.03]"
          >
            Download
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/95 md:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="border-b border-white/5 py-3 font-condensed text-xl tracking-wide text-zinc-200"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/faq"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-link-faq"
                className="border-b border-white/5 py-3 font-condensed text-xl tracking-wide text-zinc-200"
              >
                FAQ
              </Link>
              <Link
                to="/download"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-waitlist-btn"
                className="mt-4 bg-teal px-5 py-3 text-center text-sm font-bold uppercase tracking-wide text-black"
              >
                Download
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
